import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Send } from 'lucide-react';
import { api } from '@/api/client';

/**
 * Push many pickup locations to ONE courier (operator, 15-10: "push all our warehouses to Delhivery
 * id 30"). Paste the warehouse IDs (a column straight from Excel works); each is registered through the
 * same code as the per-location "Sync now", in the background and paced to Delhivery's limit.
 */
type Result = { warehouseId: number; name?: string; status: string; error?: string };
type Job = { id: string; total: number; done: number; finishedAt?: string; counts: Record<string, number>; results: Result[] };

const LABEL: Record<string, string> = {
  registered: 'Registered', already: 'Already there', skipped: 'Skipped (already registered)', failed: 'Failed', 'not-found': 'ID not found',
};

export function PushWarehousesDialog({ courier, onClose }: { courier: { id: number; name: string }; onClose: () => void }) {
  const [text, setText] = useState('');
  const [force, setForce] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const ids = Array.from(new Set((text.match(/\d+/g) ?? []).map(Number).filter((n) => n > 0)));

  const start = useMutation({
    mutationFn: async () => (await api.post(`/couriers/${courier.id}/register-warehouses`, { warehouseIds: ids, force })).data,
    onSuccess: (r: any) => { setJobId(r.jobId); toast.success(`Pushing ${r.total} location(s) to ${r.courierName}`); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not start'),
  });

  const { data: job } = useQuery<Job>({
    queryKey: ['wh-push', courier.id, jobId],
    enabled: !!jobId,
    queryFn: async () => (await api.get(`/couriers/${courier.id}/register-warehouses/${jobId}`)).data,
    refetchInterval: (q) => ((q.state.data as Job | undefined)?.finishedAt ? false : 3000),
  });
  useEffect(() => { if (job?.finishedAt) toast.success(`Finished: ${job.counts.registered ?? 0} registered, ${job.counts.failed ?? 0} failed`); }, [job?.finishedAt]);

  const problems = (job?.results ?? []).filter((r) => r.status === 'failed' || r.status === 'not-found');
  const downloadCsv = () => {
    const rows = [['warehouse_id', 'name', 'result', 'detail'], ...(job?.results ?? []).map((r) => [r.warehouseId, r.name ?? '', LABEL[r.status] ?? r.status, r.error ?? ''])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `pickup-locations-${courier.id}.csv`;
    document.body.appendChild(a); a.click(); setTimeout(() => a.remove(), 10_000);
  };

  if (!jobId) {
    return (
      <div className="space-y-3 text-sm">
        <p className="text-slate-600">Paste the <b>warehouse IDs</b> (pickup location IDs) to register with <b>{courier.name}</b> — one per line, or a whole column copied from Excel. Each goes through the same registration as the location's own <i>Sync now</i>.</p>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} className="input w-full font-mono text-xs" placeholder={'8636\n1528\n8534'} />
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
          Push again even if a location is already registered with this courier
        </label>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">{ids.length} ID(s) found{ids.length ? ` · runs in the background at Delhivery's limit (one every 4–6 s, about ${Math.ceil(ids.length / 15)}–${Math.ceil(ids.length / 10)} min)` : ''}</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 border rounded">Cancel</button>
            <button onClick={() => start.mutate()} disabled={!ids.length || start.isPending}
              className="px-3 py-1.5 rounded bg-primary text-white disabled:opacity-50 inline-flex items-center gap-1">
              {start.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Push {ids.length || ''}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const pct = job ? Math.round((job.done / Math.max(1, job.total)) * 100) : 0;
  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-center justify-between">
        <span>{job?.finishedAt ? 'Finished' : 'Pushing…'} {job ? `${job.done} / ${job.total}` : ''}</span>
        {!job?.finishedAt && <Loader2 size={14} className="animate-spin text-primary" />}
      </div>
      <div className="h-2 bg-slate-100 rounded"><div className="h-2 bg-primary rounded" style={{ width: `${pct}%` }} /></div>
      <div className="flex flex-wrap gap-2 text-xs">
        {Object.entries(job?.counts ?? {}).map(([k, v]) => (
          <span key={k} className={`px-2 py-0.5 rounded-full border ${k === 'failed' || k === 'not-found' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-slate-50 text-slate-700 border-slate-200'}`}>{LABEL[k] ?? k}: {v}</span>
        ))}
      </div>
      {problems.length > 0 && (
        <div className="max-h-56 overflow-y-auto border rounded">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500"><tr><th className="text-left px-2 py-1">ID</th><th className="text-left px-2 py-1">Location</th><th className="text-left px-2 py-1">Why</th></tr></thead>
            <tbody>{problems.map((r) => (
              <tr key={r.warehouseId} className="border-t"><td className="px-2 py-1 font-mono">{r.warehouseId}</td><td className="px-2 py-1">{r.name ?? '—'}</td><td className="px-2 py-1 text-red-700">{r.error ?? LABEL[r.status]}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <div className="flex justify-between">
        <button onClick={downloadCsv} disabled={!job?.results?.length} className="px-3 py-1.5 border rounded disabled:opacity-50">Download result (CSV)</button>
        <button onClick={onClose} className="px-3 py-1.5 border rounded">{job?.finishedAt ? 'Close' : 'Close (keeps running)'}</button>
      </div>
    </div>
  );
}
